import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { useAnalysisTypeActions } from 'redux/actions/AnalysisTypes'
import { useAnalysisActions } from 'redux/actions/Analyses'
import { useTranslation } from 'language/translator'

import { getIncreaseIsPositive, getMetricTitle } from 'utils/metric'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

import { ApplicationState } from 'redux/Stores/types'

import { AccountBody } from 'redux/reducers/Accounts'
import { selectAccounts } from 'redux/selectors/Accounts'
import { selectAnalysisTypes } from 'redux/selectors/AnalysisTypes'
import {
  selectAnalysis,
  selectAnalyses,
  selectAnalysesFetchStatus,
  TAnalysisSelectorResponse
} from 'redux/selectors/Analyses'
import { selectMetricsFetched, selectOneMetric } from 'redux/selectors/Metrics'
import { TAccount } from 'types/GlobalAccount'
import { TAnalysisTypeDto } from '@homepal/api-contract'
import { TCustomizedMetric } from 'types/GlobalMetric'
import { TAnalysisTypeResponse } from 'redux/api/AnalysisTypes'
import { TUnitPrefixOption } from 'utils/autoFormat'

export type TAnalysis = TAnalysisSelectorResponse & {
  owner: TAccount | null

  /** Null until the analysis types load, or when the type is gone. */
  analysisType: TAnalysisTypeDto<TCustomizedMetric> | null
}

type TFetchState = {
  error: boolean
  loading: boolean
}

/** An analysis with its owner and analysis type, one step deep. */
const toAnalysis = (
  analysis: TAnalysisSelectorResponse,
  accounts: AccountBody,
  analysisTypes: TAnalysisTypeResponse[]
): TAnalysis => {
  const analysisType =
    analysisTypes.find((item) => item.id === analysis.analysis_type_id) ?? null

  return {
    ...analysis,
    meta: analysis.meta?.excluded_real_estates
      ? {
          ...analysis.meta,
          excluded_real_estates: [...analysis.meta.excluded_real_estates].sort(
            sortAlphabeticalAsc
          )
        }
      : analysis.meta,
    owner: analysis.responsible_account_id
      ? accounts[analysis.responsible_account_id] ?? null
      : null,
    analysisType: analysisType
      ? {
          ...analysisType,
          excluded_real_estates: [...analysisType.excluded_real_estates].sort(
            sortAlphabeticalAsc
          )
        }
      : null
  }
}

/** What every analysis is joined with. */
const useAnalysisJoins = () => {
  const { accountBody } = useSelector(selectAccounts)
  const analysisTypes = useSelector(selectAnalysisTypes)

  return (analysis: TAnalysisSelectorResponse) =>
    toAnalysis(analysis, accountBody, analysisTypes)
}

/**
 * Fetches the analyses and the analysis types they are joined with. Only the
 * analyses count towards loading: `analysisType` is nullable, so a list can
 * render before it lands.
 */
const useAnalysesFetchState = (): TFetchState => {
  const { tryGetAllAnalysisTypes } = useAnalysisTypeActions()
  const { tryGetAllAnalyses } = useAnalysisActions()

  const fetchStatus = useSelector(selectAnalysesFetchStatus)

  useEffect(() => {
    tryGetAllAnalyses()
    tryGetAllAnalysisTypes()
  }, [tryGetAllAnalysisTypes, tryGetAllAnalyses])

  return {
    error: fetchStatus === 'error',
    loading: fetchStatus === 'idle' || fetchStatus === 'fetching'
  }
}

/** The analyses still awaiting a decision. */
const getUnhandled = (analyses: TAnalysis[]): TAnalysis[] =>
  analyses.filter((item) => !item.is_handled)

/** The analyses, newest first, fetched if they are not in the store yet. */
export const useAnalyses = (): TFetchState & {
  analyses: TAnalysis[]
  unhandled: TAnalysis[]
} => {
  const { lang } = useTranslation()
  const join = useAnalysisJoins()
  const analyses = useSelector((state: ApplicationState) =>
    selectAnalyses(state, lang)
  ).map(join)
  const { error, loading } = useAnalysesFetchState()
  const unhandled = getUnhandled(analyses)

  return { error, loading, analyses, unhandled }
}

/**
 * One analysis by id, with the same fetch state. Reads the list, so `data`
 * is null here — the detail view fetches it separately.
 */
export const useAnalysis = (
  id: string | null
): TFetchState & { analysis: TAnalysis | null } => {
  const { lang } = useTranslation()
  const join = useAnalysisJoins()
  const base = useSelector((state: ApplicationState) =>
    selectAnalysis(state, id, lang)
  )
  const { error, loading } = useAnalysesFetchState()

  return { analysis: base ? join(base) : null, error, loading }
}

/**
 * One analysis together with the signed url for its evidence. That url is
 * short-lived and only comes with a single-analysis fetch, so this asks for
 * one — use it in the views that render the evidence, not in lists.
 */
export const useAnalysisDetail = (
  id: string | null
): TFetchState & { analysis: TAnalysis | null } => {
  const { tryGetOneAnalysis } = useAnalysisActions()

  const state = useAnalysis(id)

  useEffect(() => {
    if (id) {
      tryGetOneAnalysis(id)
    }
  }, [id, tryGetOneAnalysis])

  return state
}

export type TMetricDisplay = {
  title: string
  unit: string
  unit_prefixes: TUnitPrefixOption[]
  increase_is_positive: boolean
}

/** The metric as the dashboards compose it. Null while the catalog loads. */
export const useReportMetric = (
  analysis: TAnalysis | null
): TMetricDisplay | null => {
  const { lang, translateEnum } = useTranslation()

  // Nullable so a page can call it before the analysis has loaded.
  const analysisMetric = analysis?.metric ?? null
  const metric = useSelector((state: ApplicationState) =>
    analysisMetric
      ? selectOneMetric(state, analysisMetric.id, lang, null)
      : null
  )
  const metricsFetched = useSelector(selectMetricsFetched)

  if (analysisMetric && !metric && !metricsFetched) {
    return null
  }

  // A deleted metric still leaves an analysis worth showing, untitled.
  if (!analysisMetric || !metric) {
    return {
      title: '',
      unit: '',
      unit_prefixes: [],
      increase_is_positive: getIncreaseIsPositive({ metric: null })
    }
  }

  const { settings } = analysisMetric

  return {
    title: getMetricTitle(metric, settings, lang, translateEnum),

    // raw, the value picks its own prefix from unit_prefixes
    unit: metric.unit ?? '',
    unit_prefixes: metric.unit_prefixes,
    increase_is_positive: getIncreaseIsPositive({ metric })
  }
}

/** The analyses generated from one analysis type. Empty when the id is null. */
export const useAnalysisTypeAnalyses = (
  analysisTypeId: string | null
): TFetchState & {
  analyses: TAnalysis[]
  unhandled: TAnalysis[]
  lastPublishedAt: string | null
} => {
  const { lang } = useTranslation()
  const join = useAnalysisJoins()
  const analyses = useSelector((state: ApplicationState) =>
    selectAnalyses(state, lang)
  )
    .filter((item) => item.analysis_type_id === analysisTypeId)
    .map(join)
  const { error, loading } = useAnalysesFetchState()
  const unhandled = getUnhandled(analyses)
  const lastPublishedAt = analyses.reduce<string | null>(
    (latest, analysis) =>
      latest && latest > analysis.created_at ? latest : analysis.created_at,
    null
  )

  return { error, loading, analyses, unhandled, lastPublishedAt }
}
